/**
 * Hands a fetched blob to the browser as a file download.
 *
 * Why this exists instead of the four-line `createObjectURL → a.click() →
 * revokeObjectURL` that used to sit inline in `invoice-client.ts` and
 * `catalog-client.ts`: on Android Chrome the download starts asynchronously
 * after `click()`, and revoking the object URL on the very next statement
 * cancels it — the network request had already succeeded (the server logged
 * 200 + 421 KB for the invoice PDF), yet nothing appeared on the phone, and
 * the buyer rage-clicked «Скачать PDF» eight times (PostHog session, 2026-09-18,
 * user kbek70723). Two things fix that:
 *
 *   1. the anchor is attached to the document for the click (some browsers
 *      ignore `click()` on a detached node), and
 *   2. the object URL is revoked a minute later, not immediately.
 *
 * Browsers with no `download` attribute (WebView-based in-app browsers such as
 * Instagram/Telegram on iOS) cannot save a blob at all; there the blob is
 * opened in a new tab, where the built-in viewer shows it and offers «share».
 * If even that is blocked, the current tab navigates to it — the user asked
 * for the file, and a visible PDF beats a silent no-op.
 */
export const BLOB_URL_TTL_MS = 60_000;

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const release = () => URL.revokeObjectURL(url);

  if (!('download' in HTMLAnchorElement.prototype)) {
    // Not `window.open(url, '_blank', 'noopener')`: with `noopener` in the
    // feature string the call returns null BY SPEC whether or not the tab
    // opened, so it cannot tell a blocked popup from a successful one, and the
    // "blocked" fallback below ran every time — the file opened in a new tab
    // AND the current tab navigated away from the app onto the blob. Open
    // plainly, sever the opener by hand, and only then read null as blocked.
    const tab = window.open(url, '_blank');
    if (tab) tab.opener = null;
    else window.location.assign(url);
    window.setTimeout(release, BLOB_URL_TTL_MS);
    return;
  }

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    a.remove();
    release();
  }, BLOB_URL_TTL_MS);
}
